import type { AccountModel, PriceCurrency, Propfirm, PropfirmAccount } from '../data/types';

// Helpers purs (sans JSX) partagés par les deux tables du comparateur.
// Déterministes et testables : aucune dépendance au temps réel.

/** Année de référence pour l'ancienneté — alignée sur le dataset (vérifié le 2026-07-24). */
export const REFERENCE_YEAR = 2026;

export type SeniorityTier = 'red' | 'orange' | 'yellow' | 'green';

/** Nombre d'années d'existence, ou `null` si l'année de fondation est inconnue. */
export function seniorityYears(foundedYear: number | null): number | null {
  if (foundedYear === null) return null;
  return Math.max(0, REFERENCE_YEAR - foundedYear);
}

/** Palier de couleur de la pastille d'ancienneté. */
export function seniorityTier(years: number): SeniorityTier {
  if (years < 2) return 'red';
  if (years < 3.5) return 'orange';
  if (years < 5) return 'yellow';
  return 'green';
}

/** Couleur (hex) du score de fiabilité, du rouge (faible) au vert (élevé). */
export function reliabilityColor(score: number): string {
  if (score >= 75) return '#22c55e';
  if (score >= 60) return '#84cc16';
  if (score >= 45) return '#eab308';
  if (score >= 30) return '#f97316';
  return '#ef4444';
}

const MODEL_ORDER: readonly AccountModel[] = ['1-step', '2-step', '3-step', 'instant'];

/** Modèles de comptes présents chez une firme, dans un ordre stable. */
export function presentModels(accounts: readonly PropfirmAccount[]): AccountModel[] {
  const set = new Set(accounts.map((a) => a.model));
  return MODEL_ORDER.filter((m) => set.has(m));
}

/** Coût de financement : `value` = prix par $ de capital, `fallback` = hors 2-step. */
export interface FundingCost {
  value: number;
  fallback: boolean;
}

/**
 * Coût du $ de financement : somme des prix / somme des tailles sur les comptes
 * 2-step. À défaut de 2-step, retombe sur tous les comptes non-instant (`fallback`).
 * Les firmes facturées en EUR sont exclues (prix non comparables en USD) → `null`.
 */
export function fundingCostPerDollar(
  firm: Pick<Propfirm, 'accounts' | 'priceCurrency'>,
): FundingCost | null {
  if (firm.priceCurrency === 'EUR') return null;
  const twoStep = firm.accounts.filter((a) => a.model === '2-step');
  const fallback = twoStep.length === 0;
  const pool = fallback ? firm.accounts.filter((a) => a.model !== 'instant') : twoStep;
  const totalSize = pool.reduce((sum, a) => sum + a.sizeUsd, 0);
  if (totalSize <= 0) return null;
  const totalPrice = pool.reduce((sum, a) => sum + a.priceUsd, 0);
  return { value: totalPrice / totalSize, fallback };
}

/** Coût de financement lisible : 0.005454… → "0.0055 $" (4 décimales, triable). */
export function formatFundingCost(value: number): string {
  return `${value.toFixed(4)} $`;
}

/**
 * Ratio cible de profit : drawdown max, normalisé à « 1:x » (métrique dérivée,
 * reprise de propfirmmatch). La cible totale additionne les phases renseignées ;
 * `null` si aucune cible (compte instant) ou drawdown inconnu.
 */
export function ptDdRatio(account: PropfirmAccount): string | null {
  const target = (account.targetP1Pct ?? 0) + (account.targetP2Pct ?? 0);
  if (target <= 0 || account.maxDrawdownPct == null) return null;
  return `1:${parseFloat((account.maxDrawdownPct / target).toFixed(2))}`;
}

function trimNumber(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

/** Montant compact en USD : 400000 → "$400K", 1750000 → "$1.75M". */
export function formatCompactUsd(n: number): string {
  if (n >= 1_000_000) return `$${trimNumber(n / 1_000_000)}M`;
  if (n >= 1_000) return `$${trimNumber(n / 1_000)}K`;
  return `$${n}`;
}

/** Prix d'un challenge dans sa devise (USD par défaut, EUR pour certaines firmes). */
export function formatPrice(amount: number, currency: PriceCurrency = 'USD'): string {
  const rounded = Math.round(amount);
  return currency === 'EUR' ? `${rounded} €` : `$${rounded}`;
}

/** Nombre d'avis compact : 46000 → "46k", 1246 → "1.2k", 580 → "580". */
export function formatReviews(n: number): string {
  if (n >= 10_000) return `${Math.round(n / 1_000)}k`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${n}`;
}

// Mapping siège (texte du dataset, en français) → code ISO-2 pour le drapeau.
// L'ordre prime : les entrées les plus spécifiques d'abord.
const COUNTRY_KEYWORDS: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['CZ', ['tchéquie', 'prague']],
  ['AE', ['eau', 'émirats', 'emirats', 'dubaï', 'dubai']],
  ['IL', ['israël', 'israel']],
  ['ES', ['espagne', 'palmas']],
  ['CY', ['chypre', 'limassol']],
  ['NZ', ['nouvelle-zélande', 'auckland']],
  ['AU', ['australie', 'sydney', 'melbourne']],
  ['VC', ['saint-vincent', 'grenadines']],
  ['US', ['usa', 'états-unis', 'etats-unis', 'chicago', 'new york', 'texas', 'austin', 'dallas']],
  ['GB', ['royaume-uni', 'londres', 'united kingdom', ', uk']],
];

/** Code pays ISO-2 déduit du siège, ou `null` si non reconnu. */
export function countryCodeFromHeadquarters(headquarters: string | null): string | null {
  if (!headquarters) return null;
  const hq = headquarters.toLowerCase();
  for (const [code, keywords] of COUNTRY_KEYWORDS) {
    if (keywords.some((k) => hq.includes(k))) return code;
  }
  return null;
}

/** URL de favicon (tentative runtime discrète) à partir du site officiel. */
export function faviconUrl(website: string): string {
  try {
    const { hostname } = new URL(website);
    return `https://www.google.com/s2/favicons?domain=${hostname}&sz=64`;
  } catch {
    return '';
  }
}

/** Initiales pour le fallback avatar (1 ou 2 lettres). */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
